import { AitTableData, AsupInternalTable } from "aic";
import { CommentScope } from "components/common/comments/CommentScope";
import { v3EditorProps } from "components/common/v3EditorProps";
import { AppSettingsContext } from "contexts/AppSettingsContext";
import { HierarchyContext } from "contexts/HierarchyContext";
import { getExternalLists } from "contexts/tlf/getExternalLists";
import { getExternalSingles } from "contexts/tlf/getExternalSingles";
import { getGroupTemplates } from "contexts/tlf/getGroupTemplates";
import { UPDATE_TLF } from "contexts/tlf/interface";
import { TlfContext } from "contexts/TlfContext";
import { IEditorV3 } from "editor-v3";
import { eHierarchyLevel } from "functions/api/enums";
import { ReactNode, useContext } from "react";

/**
 * Renders the editable table listing for a TLF item using the internal AIT
 * table component. Integrates external lists, singles and group templates
 * from hierarchy context.
 */
export const TableListing = (): ReactNode => {
  const appSettings = useContext(AppSettingsContext);
  const hierarchyContext = useContext(HierarchyContext);
  const tlfContext = useContext(TlfContext);
  const tlfData = tlfContext?.tlfData;

  const externalSingles =
    tlfData && hierarchyContext && hierarchyContext.hierarchy
      ? getExternalSingles(
          tlfData.tocNumber,
          tlfData.releaseStatus,
          tlfData.domain,
          tlfData.singles,
          hierarchyContext.hierarchy.level,
          hierarchyContext.hierarchy.singles,
          hierarchyContext.hierarchy.domainReplacements,
          hierarchyContext.parentTemplates.singles,
          hierarchyContext.parentTemplates.domainReplacements,
        )
      : [];

  const externalLsts =
    tlfData && hierarchyContext && hierarchyContext.hierarchy
      ? getExternalLists(
          tlfData.primaryHierarchyId,
          tlfData.primaryHierarchyLevel,
          tlfData.releaseStatus,
          hierarchyContext.hierarchy.lists,
          hierarchyContext.parentTemplates.lists,
        )
      : [];

  const groupTemplates = getGroupTemplates(
    hierarchyContext?.hierarchy?.rowGroups,
    hierarchyContext?.parentTemplates.rowGroups,
  );

  return !hierarchyContext || !tlfData ? null : (
    <CommentScope
      id="tlf-table-comment"
      activeContext="TlfItem"
      wrap
      iconPosition={{ top: "16px", left: "38px" }}
      commentLabel="New table comment"
    >
      <AsupInternalTable
        id="tlf-table"
        tableData={tlfData.body as AitTableData<IEditorV3>}
        isEditable={!tlfContext.readOnly}
        onChange={(ret) =>
          tlfContext.dispatch({
            operation: UPDATE_TLF,
            tlfData: { body: ret },
            calledFrom: "TableListing:setTableData",
          })
        }
        noTrafficLights={
          !tlfContext.showTrafficLights ||
          tlfData.primaryHierarchyLevel !== eHierarchyLevel.Report ||
          tlfData.coreTrafficLightSwitch === "DoNotCalculate"
        }
        style={{ ...appSettings.defaultTableStyle, margin: "1rem" }}
        showCellBorders={true}
        externalLists={externalLsts}
        externalSingles={externalSingles}
        groupTemplates={groupTemplates}
        colWidthMod={appSettings.colWidthMod}
        defaultCellWidth={appSettings.defaultColumnWidth}
        editorProps={v3EditorProps}
        tlfViewMode={tlfContext.tlfViewMode}
      />
    </CommentScope>
  );
};

TableListing.displayName = "TableListing";
